/**
 * PageHeader Component
 * Unified page header with gradient title, subtitle, and optional actions
 * Provides consistent styling across Dashboard, Analytics, and Incidents pages
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../design-system';

export interface PageHeaderProps {
  /** Main title text */
  title: string;
  /** Description text below the title */
  subtitle?: string;
  /** Service count or other metric to display */
  metric?: string | number;
  /** Right-aligned action content */
  actions?: React.ReactNode;
  /** Optional icon to display next to title */
  icon?: React.ReactNode;
}

/**
 * PageHeader Component
 * Provides a consistent header layout across all pages with:
 * - Gradient title text
 * - Subtitle with optional metric
 * - Action slot for controls/buttons
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  metric,
  actions,
  icon,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const textSecondary = isDark 
    ? tokens.colors.gray[400] 
    : tokens.colors.semantic.textSecondary;

  return (
    <Box sx={{ mb: 4 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Title Section */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            {icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: tokens.radius.md,
                  background: tokens.colors.gradients.primary,
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: tokens.colors.gradients.primary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
          </Box>
          
          {(subtitle || metric) && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: textSecondary,
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {subtitle}
              {metric && (
                <>
                  {subtitle && ' • '}
                  <Box 
                    component="span" 
                    sx={{ 
                      fontWeight: 600,
                      color: isDark ? tokens.colors.primary[400] : tokens.colors.primary[600],
                    }}
                  >
                    {metric}
                  </Box>
                </>
              )}
            </Typography>
          )}
        </Box>

        {/* Actions Section */}
        {actions && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              flexShrink: 0,
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
